import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { logsService, Log } from "../services/logs.service";
import { providersService, ProviderSuggestion, LIBELLES_ENERGIE, type Energie } from "../services/providers.service";
import { expenseCategoriesService, ExpenseCategory } from "../services/expense-categories.service";
import { useProviders } from "../hooks/useProviders";
import { usePdfPreview } from "../hooks/usePdfPreview";
import { openFileInNewTab as openFile } from "../utils/file";
import PdfPreview from "../components/PdfPreview";
import "../styles/ErrorLogDetailsPage.css";
import { getErrorMessage } from '../utils/error';
import { pourcentageVersFraction } from '../utils/format';

const ErrorLogDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [log, setLog] = useState<Log | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isStackTraceExpanded, setIsStackTraceExpanded] = useState<boolean>(false);
  const [selectedProvider, setSelectedProvider] = useState<string>("");
  const [amount, setAmount] = useState<string>("");
  const [initialAmount, setInitialAmount] = useState<string>("");
  // La date de LA FACTURE. Elle n'était pas demandée : le serveur retombait sur
  // la date du jour, et une facture de décembre rattrapée en janvier changeait
  // d'exercice.
  const [invoiceDate, setInvoiceDate] = useState<string>("");
  const [consumptionQuantity, setConsumptionQuantity] = useState<string>("");
  const [consumptionUnit, setConsumptionUnit] = useState<string>("kWh");
  // Relevé ou estimation : sans elle, la facture masque l'historique de
  // consommation (ticket #14). « » = non précisé.
  const [consumptionEstimated, setConsumptionEstimated] = useState<string>("");
  const [periodStartDate, setPeriodStartDate] = useState<string>("");
  const [periodEndDate, setPeriodEndDate] = useState<string>("");
  const [validating, setValidating] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [validationSuccess, setValidationSuccess] = useState<boolean>(false);
  const [redirectCountdown, setRedirectCountdown] = useState<number>(3);
  const [dismissing, setDismissing] = useState<boolean>(false);

  const { providers, loading: loadingProviders, error: providersError, refetch: refetchProviders } = useProviders(!!log);
  const pdfPreview = usePdfPreview(log?.colocId, log?.fileName);

  // Create provider modal
  const [showCreateProviderModal, setShowCreateProviderModal] = useState(false);
  const [newProviderName, setNewProviderName] = useState("");
  const [newExpenseCategoryId, setNewExpenseCategoryId] = useState("");
  const [newRequiresConsumptionPeriod, setNewRequiresConsumptionPeriod] = useState(false);
  // « » : le serveur la déduit du nom quand le nom la dit (#15).
  const [newEnergy, setNewEnergy] = useState<Energie | "">("");
  const [newPrcOfInvoice, setNewPrcOfInvoice] = useState("");
  const [newAmountOfInvoice, setNewAmountOfInvoice] = useState("");
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [suggestions, setSuggestions] = useState<ProviderSuggestion[]>([]);
  const [filteredSuggestions, setFilteredSuggestions] = useState<ProviderSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [creatingProvider, setCreatingProvider] = useState(false);
  const [createProviderError, setCreateProviderError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLog = async () => {
      try {
        if (!id) {
          throw new Error("Log ID is required");
        }
        const logData = await logsService.getLogById(id);
        setLog(logData);

        // Pre-fill form fields from LLM data if available
        const llmData = logData.context?.llmData;
        if (llmData) {
          if (llmData.totalAmount) {
            setAmount(llmData.totalAmount.toString());
            setInitialAmount(llmData.totalAmount.toString());
          }
          if (llmData.periodStartDate) setPeriodStartDate(llmData.periodStartDate);
          if (llmData.periodEndDate) setPeriodEndDate(llmData.periodEndDate);
          if (llmData.date) setInvoiceDate(llmData.date);
          if (llmData.consumptionQuantity != null) {
            setConsumptionQuantity(String(llmData.consumptionQuantity));
          }
          if (llmData.consumptionUnit) setConsumptionUnit(llmData.consumptionUnit);
          if (llmData.consumptionEstimated != null) {
            setConsumptionEstimated(String(llmData.consumptionEstimated));
          }
        }

        setLoading(false);
      } catch (err) {
        setError("Échec du chargement des détails du journal");
        setLoading(false);
      }
    };
    fetchLog();
  }, [id]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (validationSuccess && redirectCountdown > 0) {
      timer = setTimeout(() => {
        setRedirectCountdown((prev) => prev - 1);
      }, 1000);
    } else if (validationSuccess && redirectCountdown === 0) {
      navigate("/pending-invoices");
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [validationSuccess, redirectCountdown, navigate]);

  // Load categories and suggestions for create provider modal
  useEffect(() => {
    const loadCreateProviderData = async () => {
      try {
        const [cats, sugg] = await Promise.all([
          expenseCategoriesService.getExpenseCategories(),
          providersService.getSuggestions(),
        ]);
        setCategories(cats);
        setSuggestions(sugg);
      } catch {
        // silent
      }
    };
    loadCreateProviderData();
  }, []);

  // Create provider modal helpers
  const handleNewProviderNameInput = (value: string) => {
    setNewProviderName(value);
    if (value.length > 0) {
      const matches = suggestions.filter((s) =>
        s.providerName.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredSuggestions(matches);
      setShowSuggestions(matches.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion: ProviderSuggestion) => {
    setNewProviderName(suggestion.providerName);
    setShowSuggestions(false);
    setNewRequiresConsumptionPeriod(suggestion.requiresConsumptionPeriod);
    setNewEnergy(suggestion.energy ?? "");
    const cat = categories.find(
      (c) => c.name.toLowerCase() === suggestion.expenseCategory.toLowerCase()
    );
    if (cat) setNewExpenseCategoryId(cat.id);
  };

  const openCreateProviderModal = () => {
    // Pre-fill provider name from LLM data if available
    const llmProvider = log?.context?.llmData?.provider || "";
    setNewProviderName(llmProvider);
    setNewExpenseCategoryId("");
    setNewRequiresConsumptionPeriod(false);
    setNewEnergy("");
    setNewPrcOfInvoice("");
    setNewAmountOfInvoice("");
    setCreateProviderError(null);
    setShowCreateProviderModal(true);

    // Check if LLM provider name matches a suggestion
    if (llmProvider) {
      const match = suggestions.find(
        (s) => s.providerName.toLowerCase() === llmProvider.toLowerCase()
      );
      if (match) {
        setNewRequiresConsumptionPeriod(match.requiresConsumptionPeriod);
        setNewEnergy(match.energy ?? "");
        const cat = categories.find(
          (c) => c.name.toLowerCase() === match.expenseCategory.toLowerCase()
        );
        if (cat) setNewExpenseCategoryId(cat.id);
      }
    }
  };

  const handleCreateProvider = async () => {
    if (!newProviderName.trim()) return;
    setCreatingProvider(true);
    setCreateProviderError(null);
    try {
      const created = await providersService.createProvider({
        providerName: newProviderName.trim(),
        colocId: log?.colocId || undefined,
        expenseCategoryId: newExpenseCategoryId || undefined,
        requiresConsumptionPeriod: newRequiresConsumptionPeriod,
        ...(newEnergy ? { energy: newEnergy } : {}),
        prcOfInvoice: newPrcOfInvoice ? pourcentageVersFraction(newPrcOfInvoice) : undefined,
        amountOfInvoice: newAmountOfInvoice ? parseFloat(newAmountOfInvoice) : undefined,
      });
      setShowCreateProviderModal(false);
      // Refresh providers list and auto-select the new one
      await refetchProviders();
      setSelectedProvider(created.id);
    } catch (err) {
      setCreateProviderError(getErrorMessage(err, "Erreur lors de la création du fournisseur"));
    } finally {
      setCreatingProvider(false);
    }
  };

  const handleBack = () => navigate("/pending-invoices");

  const handleDismiss = async () => {
    if (!window.confirm("Voulez-vous vraiment rejeter cette facture ?")) return;

    setDismissing(true);
    try {
      await logsService.dismissLog(id!);
      navigate("/pending-invoices");
    } catch (err) {
      setValidationError(getErrorMessage(err, "Erreur lors du rejet"));
    } finally {
      setDismissing(false);
    }
  };
  const toggleStackTrace = () => setIsStackTraceExpanded(!isStackTraceExpanded);

  const handleOpenFileInNewTab = () => {
    if (!log?.fileName || !log?.colocId) return;
    openFile(log.colocId, log.fileName);
  };

  const handleProviderChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedProvider(e.target.value);
    // Fournisseur partagé : le montant proposé est la part de la colocation,
    // pas le total. Valider sans retoucher imputait 100 % (ticket #14).
    const fournisseur = providers.find((p) => p.id === e.target.value);
    const total = parseFloat(initialAmount);
    if (fournisseur?.amountOfInvoice) {
      setAmount(String(fournisseur.amountOfInvoice));
    } else if (fournisseur?.prcOfInvoice && !isNaN(total)) {
      setAmount(String(Math.round(total * fournisseur.prcOfInvoice * 100) / 100));
    }
    setValidationError(null);
    setValidationSuccess(false);
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAmount(e.target.value);
    setValidationError(null);
    setValidationSuccess(false);
  };

  const handleInitialAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInitialAmount(e.target.value);
    setValidationError(null);
    setValidationSuccess(false);
  };

  const validateForm = (): boolean => {
    if (!selectedProvider) {
      setValidationError("Veuillez sélectionner un fournisseur");
      return false;
    }
    if (!initialAmount) {
      setValidationError("Veuillez entrer un montant initial");
      return false;
    }
    if (!amount) {
      setValidationError("Veuillez entrer un montant final");
      return false;
    }
    const initialAmountValue = parseFloat(initialAmount);
    const finalAmountValue = parseFloat(amount);
    if (isNaN(initialAmountValue) || initialAmountValue === 0) {
      setValidationError("Le montant initial doit être un nombre non nul");
      return false;
    }
    if (isNaN(finalAmountValue) || finalAmountValue === 0) {
      setValidationError("Le montant final doit être un nombre non nul");
      return false;
    }
    // Négatif = avoir (ticket #47). Seul cas légitime : une erreur de signe ne
    // doit pas passer en silence.
    if ((initialAmountValue < 0 || finalAmountValue < 0)
        && !window.confirm("Ce montant négatif correspond à un avoir ?")) {
      return false;
    }
    return true;
  };

  const handleValidateInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);
    setValidationSuccess(false);

    if (!validateForm()) return;

    setValidating(true);
    try {
      const finalAmount = parseFloat(amount);
      const initialAmountValue = parseFloat(initialAmount);
      const updatedLog = await logsService.validateInvoice(
        id!,
        selectedProvider,
        finalAmount,
        initialAmountValue,
        periodStartDate || undefined,
        periodEndDate || undefined,
        invoiceDate || undefined,
        consumptionQuantity === "" ? undefined : parseFloat(consumptionQuantity),
        consumptionQuantity === "" ? undefined : consumptionUnit,
        consumptionEstimated === "" ? undefined : consumptionEstimated === "true"
      );
      setLog(updatedLog);
      setValidationSuccess(true);
      setRedirectCountdown(3);
      setSelectedProvider("");
      setAmount("");
      setInitialAmount("");
      setPeriodStartDate("");
      setPeriodEndDate("");
      setInvoiceDate("");
      setConsumptionQuantity("");
    } catch (err) {
      setValidationError(getErrorMessage(err, "Échec de la validation de la facture. Veuillez réessayer."));
    } finally {
      setValidating(false);
    }
  };

  if (loading) {
    return <div className="loading">Chargement...</div>;
  }

  if (error || !log) {
    return <div className="error">{error || 'Journal introuvable'}</div>;
  }

  return (
    <div className="error-log-details-page">
      <div className="error-log-header">
        <button className="back-button" onClick={handleBack}>
          &larr; Retour aux factures en attente
        </button>
        <h1>Facture en attente de traitement</h1>
      </div>
      
      <div className="error-log-content">
        <div className="error-log-info">
          <div className="info-section">
            <div className="error-log-title">
              <h2>Détails de l'erreur</h2>
              <div className="log-meta">
                <span className="log-meta-item">
                  <strong>Fichier:</strong> {log.fileName}
                </span>
                <span className="log-meta-item">
                  <strong>Date:</strong> {new Date(log.createdAt).toLocaleString()}
                </span>
                <span className="log-meta-item status">
                  <strong>Statut:</strong> <span className="error-status">{log.status}</span>
                </span>
              </div>
            </div>
            
            <div className="error-message">
              <h3>Message d'erreur</h3>
              <div className="error-text">{log.errorMessage || 'Aucun message d\'erreur'}</div>
            </div>
            
            <div className="stack-trace">
              <div className="stack-trace-header" onClick={toggleStackTrace}>
                <h3>Trace d'erreur</h3>
                <button className="toggle-button">
                  {isStackTraceExpanded ? 'Masquer' : 'Afficher'}
                </button>
              </div>
              {isStackTraceExpanded && (
                <pre className="stack-trace-text">{log.stackTrace || 'Aucune trace d\'erreur'}</pre>
              )}
            </div>
            
            {log.status === 'error' && (
              <div className="manual-invoice-form">
                <h3>Traitement manuel de la facture</h3>
                {validationSuccess && (
                  <div className="validation-success">
                    Facture créée avec succès ! Redirection vers les journaux dans {redirectCountdown} secondes...
                  </div>
                )}
                {validationError && (
                  <div className="validation-error">{validationError}</div>
                )}
                {providersError ? (
                  <div className="providers-error">{providersError}</div>
                ) : loadingProviders ? (
                  <div className="loading-providers">Chargement des fournisseurs...</div>
                ) : (
                  <form onSubmit={handleValidateInvoice}>
                    <div className="form-group">
                      <label htmlFor="provider">Fournisseur:</label>
                      <select
                        id="provider"
                        value={selectedProvider}
                        onChange={handleProviderChange}
                        required
                        disabled={validating || validationSuccess}
                      >
                        <option value="">Sélectionner un fournisseur</option>
                        {providers.map(provider => (
                          <option key={provider.id} value={provider.id}>
                            {provider.providerName}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="create-provider-link"
                        onClick={openCreateProviderModal}
                        disabled={validating || validationSuccess}
                      >
                        + Créer un fournisseur
                      </button>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="initialAmount">Montant initial:</label>
                      <input 
                        type="number" 
                        id="initialAmount" 
                        value={initialAmount} 
                        onChange={handleInitialAmountChange}
                        step="0.01"
                        required
                        placeholder="Montant initial"
                        disabled={validating || validationSuccess}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="amount">Montant final:</label>
                      <input
                        type="number"
                        id="amount"
                        value={amount}
                        onChange={handleAmountChange}
                        step="0.01"
                        required
                        placeholder="Montant final"
                        disabled={validating || validationSuccess}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="invoiceDate">Date de la facture:</label>
                      <input
                        type="date"
                        id="invoiceDate"
                        value={invoiceDate}
                        onChange={(e) => setInvoiceDate(e.target.value)}
                        disabled={validating || validationSuccess}
                      />
                      <small className="field-hint">
                        Celle imprimée sur la facture. Laissée vide, la dépense
                        prend la date du jour et change de mois.
                      </small>
                    </div>

                    {providers.find(p => p.id === selectedProvider)?.requiresConsumptionPeriod && (
                      <>
                        <div className="form-group">
                          <label htmlFor="periodStartDate">Debut de periode:</label>
                          <input
                            type="date"
                            id="periodStartDate"
                            value={periodStartDate}
                            onChange={(e) => setPeriodStartDate(e.target.value)}
                            disabled={validating || validationSuccess}
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="periodEndDate">Fin de periode:</label>
                          <input
                            type="date"
                            id="periodEndDate"
                            value={periodEndDate}
                            onChange={(e) => setPeriodEndDate(e.target.value)}
                            disabled={validating || validationSuccess}
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="consumptionQuantity">Consommation (optionnel):</label>
                          <div className="consumption-fields">
                            <input
                              type="number"
                              id="consumptionQuantity"
                              value={consumptionQuantity}
                              onChange={(e) => setConsumptionQuantity(e.target.value)}
                              step="0.001"
                              min="0"
                              placeholder="Quantité"
                              disabled={validating || validationSuccess}
                            />
                            <select
                              id="consumptionUnit"
                              value={consumptionUnit}
                              onChange={(e) => setConsumptionUnit(e.target.value)}
                              aria-label="Unité de consommation"
                              disabled={validating || validationSuccess}
                            >
                              <option value="kWh">kWh</option>
                              <option value="m3">m³</option>
                              <option value="MWh">MWh</option>
                            </select>
                            <select
                              id="consumptionEstimated"
                              value={consumptionEstimated}
                              onChange={(e) => setConsumptionEstimated(e.target.value)}
                              aria-label="Nature du relevé"
                              disabled={validating || validationSuccess}
                            >
                              <option value="">Relevé ou estimation ?</option>
                              <option value="false">Relevé</option>
                              <option value="true">Estimation</option>
                            </select>
                          </div>
                        </div>
                      </>
                    )}

                    <div className="form-actions">
                      <button
                        type="submit"
                        className={`validate-button ${validating ? 'validating' : ''}`}
                        disabled={validating || validationSuccess || dismissing}
                      >
                        {validating ? (
                          <>
                            <span className="spinner"></span>
                            Validation en cours...
                          </>
                        ) : 'Valider la facture'}
                      </button>
                      <button
                        type="button"
                        className="dismiss-detail-button"
                        onClick={handleDismiss}
                        disabled={validating || validationSuccess || dismissing}
                      >
                        {dismissing ? 'Rejet en cours...' : 'Rejeter la facture'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>
        </div>
        
        <div className="file-preview-section">
          <h2>Aperçu du fichier</h2>
          <PdfPreview
            isPdf={pdfPreview.isPdf}
            isImage={pdfPreview.isImage}
            dataUrl={pdfPreview.dataUrl}
            directFileUrl={pdfPreview.directFileUrl}
            fileError={pdfPreview.fileError}
            fileName={log.fileName}
            onOpenInNewTab={handleOpenFileInNewTab}
          />
        </div>
      </div>

      {/* Create provider modal */}
      {showCreateProviderModal && (
        <div className="modal-overlay" onClick={() => setShowCreateProviderModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Créer un fournisseur</h3>

            <div className="form-group">
              <label>Nom du fournisseur</label>
              <div className="autocomplete-wrapper">
                <input
                  type="text"
                  value={newProviderName}
                  onChange={(e) => handleNewProviderNameInput(e.target.value)}
                  onFocus={() => {
                    if (newProviderName.length > 0) {
                      const matches = suggestions.filter((s) =>
                        s.providerName.toLowerCase().includes(newProviderName.toLowerCase())
                      );
                      setFilteredSuggestions(matches);
                      setShowSuggestions(matches.length > 0);
                    }
                  }}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  placeholder="Ex: EDF, Orange..."
                />
                {showSuggestions && (
                  <ul className="suggestions-list">
                    {filteredSuggestions.map((s) => (
                      <li key={s.providerName} onMouseDown={() => handleSuggestionClick(s)}>
                        <strong>{s.providerName}</strong>
                        <span className="suggestion-cat">{s.expenseCategory}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Catégorie de dépense</label>
              <select
                value={newExpenseCategoryId}
                onChange={(e) => setNewExpenseCategoryId(e.target.value)}
              >
                <option value="">-- Aucune --</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="newEnergy">Énergie (graphiques de consommation)</label>
              <select
                id="newEnergy"
                value={newEnergy}
                onChange={(e) => setNewEnergy(e.target.value as Energie | "")}
              >
                <option value="">D&apos;après le nom (GRDF, EDF, Iléo…)</option>
                {(Object.keys(LIBELLES_ENERGIE) as Energie[]).map((cle) => (
                  <option key={cle} value={cle}>{LIBELLES_ENERGIE[cle]}</option>
                ))}
              </select>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={newRequiresConsumptionPeriod}
                  onChange={(e) => setNewRequiresConsumptionPeriod(e.target.checked)}
                />
                Nécessite une période de consommation
              </label>
            </div>

            <div className="form-group">
              <label>Pourcentage de la facture (%)</label>
              <input
                type="number"
                step="0.01"
                max="100"
                value={newPrcOfInvoice}
                onChange={(e) => setNewPrcOfInvoice(e.target.value)}
                placeholder="Optionnel"
              />
            </div>

            <div className="form-group">
              <label>Montant fixe (€)</label>
              <input
                type="number"
                step="0.01"
                value={newAmountOfInvoice}
                onChange={(e) => setNewAmountOfInvoice(e.target.value)}
                placeholder="Optionnel"
              />
            </div>

            {createProviderError && <div className="modal-error">{createProviderError}</div>}

            <div className="modal-actions">
              <button
                className="cancel-button"
                onClick={() => setShowCreateProviderModal(false)}
                disabled={creatingProvider}
              >
                Annuler
              </button>
              <button
                className="confirm-button"
                onClick={handleCreateProvider}
                disabled={!newProviderName.trim() || creatingProvider}
              >
                {creatingProvider ? "Création..." : "Créer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ErrorLogDetailsPage;
